import {api} from "@/shared/api";
import {Combobox} from "@/shared/ui/combobox";
import {FieldObserver} from "@/shared/ui/fieldObserver";
import {FormUtil} from "@/shared/forms/formUtil";
import {htmlEscape} from "@/shared/utils/string";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import * as Button from "@/shared/utils/button";
import {copyOfObject, fillOvertimeDeductionAccountOrder, serializeObjectToArray} from "@/shared/utils/misc";
import {DOMUtil} from "@/shared/utils/dom";
import {SecurityStatus} from "@/shared/securityStatus";
import {formatDate, formatDecimalNumber, formatDuration, parseISODate} from "@/shared/utils/datefunctions";
import {CheckForWorkScheduleModelChangeResponse, ErrorResponse, HolidayCalendar, User, WorkScheduleModel} from "@/shared/timr";
import {OvertimeDeductionAccountType, WorkScheduleModelType} from "@/shared/timrTypes";
import Modal = BootstrapModal.Modal;

export class UserChangeConfigurationDialogHandler {
	public static readonly EVENT_SAVED_CONFIGURATION = "event_saved_configuration";
	private readonly dom: JQuery;
	private readonly modal: Modal;
	private user: User;
	private readonly fieldObserver: FieldObserver<User>;
	private originalWorkScheduleModelId: number | null;
	private readonly workScheduleModelComboBox: Combobox<WorkScheduleModel>;
	private holidayCalendarSelect: JQuery;
	private customOvertimeDeductionAccountOrder: OvertimeDeductionAccountType[];
	private readonly userWorkScheduleModelAlternatingStartWeekSelect = $("#userWorkScheduleModelAlternatingStartWeek");
	private currentWorkScheduleModelWeekNumber: string;

	constructor() {
		this.dom = $("#userChangeConfigurationDialog");
		this.modal = new Modal({
			title: "userChangeConfigurationDialog.title",
			message: this.dom,
			size: "modal-xl",
			buttons: [
				{
					text: "dialog.save",
					action: (_m, button) => {
						this.saveUser(button);
					},
					css: "btn-primary save-button"
				},
				{
					text: "dialog.cancel"
				}
			]
		});

		this.fieldObserver = new FieldObserver();

		this.workScheduleModelComboBox = new Combobox<WorkScheduleModel>({
			items: [],
			formatter: (wsm) => wsm.name,
			idElement: $("#userWorkScheduleModelId"),
			filterElement: $("#userWorkScheduleModelFilter"),
			caretElement: $("#userWorkScheduleModelCaret"),
			clearable: false
		});
		$(this.workScheduleModelComboBox)
			.on(Combobox.EVENT_CHANGED, () => this.onWorkScheduleModelChange());
	}

	private setupListener() {
		this.setupCheckboxFieldObserver();
	}

	private updateAlertMessage() {
		const alert = this.dom.find("#userChangeConfigurationDialogAlert");
		if (this.user.nextTimeAccountBalanceFromDate) {
			alert
				.show()
				.html(messages.withArguments("userChangeConfigurationDialog.alert", formatDate(parseISODate(this.user.nextTimeAccountBalanceFromDate))));
		} else {
			alert.hide();
		}
	}

	private updateTitle() {
		let message = messages["userChangeConfigurationDialog.title"];

		if (this.user.fullname) {
			message += ": " + this.user.fullname;
		}

		this.modal.setTitle(message, true);
	}

	// DEVNOTE: This method must be called before setWorkScheduleModels and setHolidayCalendars
	public setUser(user: User) {
		this.user = copyOfObject(user);
		this.fieldObserver.setModel(this.user);
		this.updateCheckboxes(this.user);
		this.originalWorkScheduleModelId = this.user.workScheduleModelId;
		this.customOvertimeDeductionAccountOrder = this.user.overtimeDeductionAccountOrder;

		this.initValues();
		this.setSaveButtonLabel(messages["dialog.save"]);
		this.updateAlertMessage();
		this.updateTitle();
		this.setupListener();
		this.initDeductFromAccountSelects();
		$("#userWorkScheduleModelAlternatingStartWeekInfo")
			.text(messages.withArguments("userChangeConfigurationDialog.currentStartWeek.on",
				formatDate(parseISODate(this.user.nextTimeAccountBalanceFromDate)))
			);
	}

	// DEVNOTE: This method must be called after setUser
	public setWorkScheduleModels(workScheduleModels: WorkScheduleModel[]) {
		this.workScheduleModelComboBox.updateItems(
			workScheduleModels,
			workScheduleModels.find(wsm => wsm.id === this.originalWorkScheduleModelId),
			true
		);
		this.onWorkScheduleModelChange();
		this.loadCurrentWorkScheduleModelWeekNumber();
	}

	// DEVNOTE: This method must be called after setUser
	public setHolidayCalendars(holidayCalendars: HolidayCalendar[]) {
		this.holidayCalendarSelect = this.dom.find("#userChangeConfigurationDialogUserHolidayCalendar");
		DOMUtil.fillSelect(this.holidayCalendarSelect, holidayCalendars, false, (i: HolidayCalendar) => DOMUtil.createOption("" + i.id, i.description), true);
		this.holidayCalendarSelect.val(this.user.holidayCalendarId);
	}

	private checkUnbalancedWorkingTimes(): JQueryPromise<{ applyCutsByNormalWorkingTimes: boolean }> {
		const deferred = $.Deferred<{
			applyCutsByNormalWorkingTimes: boolean
		}>();

		const workScheduleModelChanged = this.hasWorkScheduleModelSelectionChanged();

		if (SecurityStatus.isTimeAccountAllowed() && workScheduleModelChanged) {
			api("admin/checkForUserWorkScheduleModelChange.json")
				.get({
					userId: this.user.id,
					newWorkScheduleModelId: this.workScheduleModelComboBox?.getSelectedItem()?.id
				})
				.done((data: CheckForWorkScheduleModelChangeResponse) => {
					if (data.overlappingWorkingTimeRequestsCount === 0 && data.overlappingWorkingTimeDateSpansCount === 0 &&
						data.workingTimesForRequests === 0 && data.changeableWorkingTimesForRequests === 0 &&
						data.workingTimesForWorkScheduleModel === 0 && data.workingTimeDateSpansAfterLastTimeAccountBalance === 0) {
						deferred.resolve({applyCutsByNormalWorkingTimes: false});
					} else if (data.overlappingWorkingTimeRequestsCount > 0) {
						let extraMsg = "";
						for (const wtr of data.overlappingWorkingTimeRequests) {
							extraMsg += messages.withArguments(
								"dialog.update.userWorkScheduleModel.overlappingWorkingTimeRequests.message.overlappingRecord",
								htmlEscape(wtr.user.fullname),
								formatDate(parseISODate(wtr.user.lastTimeAccountBalance)),
								messages[`workingTimeTypeCategory.${wtr.workingTimeType.category}`],
								formatDate(parseISODate(wtr.start)),
								formatDate(parseISODate(wtr.end))
							);
						}
						BootstrapModal.warn({
							title: "dialog.update.userWorkScheduleModel.foundVacations.title",
							message: messages.withArguments("dialog.update.userWorkScheduleModel.overlappingWorkingTimeRequests.message", extraMsg),
							escapeText: false,
							onClose: () => void deferred.reject()
						});
					} else if (data.overlappingWorkingTimeDateSpansCount > 0) {
						BootstrapModal.warn({
							title: "dialog.update.userWorkScheduleModel.foundVacations.title",
							message: "dialog.update.userWorkScheduleModel.overlappingWorkingTimeDateSpans.message",
							escapeText: false,
							size: "modal-lg",
							onClose: () => void deferred.reject()
						});
					} else if (data.workingTimesForRequests > data.changeableWorkingTimesForRequests) {
						// block work schedule model change: there are approved requests where the WorkingTime recordings are not changeable anymore
						BootstrapModal.warn({
							title: "dialog.update.userWorkScheduleModel.foundVacations.title",
							message: "dialog.update.userWorkScheduleModel.foundVacations.message",
							onClose: () => void deferred.reject()
						});
					} else if (data.workDaysChanged && (data.workingTimesForWorkScheduleModel > 0 || data.workingTimeDateSpansAfterLastTimeAccountBalance > 0)) {
						// block work schedule model change: the work days changed and there are half/full day WorkingTime recordings for the new work schedule model
						BootstrapModal.warn({
							title: "dialog.update.userWorkScheduleModel.foundVacations.title",
							message: "dialog.update.userWorkScheduleModel.message",
							onClose: () => void deferred.reject()
						});
					} else if (data.workingTimesForRequests > 0) {
						// work model change is allowed but a confirmation dialog has to be shown (changeable working times for requests)

						BootstrapModal.confirm({
							title: "dialog.update.userWorkScheduleModel.title",
							message: "dialog.update.workScheduleModel.adaptRequests.message",
							yesLabel: "dialog.update.workScheduleModel.buttonChange",
							noLabel: "confirmation.cancel",
							yesAction: (modal: BootstrapModal.Modal) => {
								deferred.resolve({applyCutsByNormalWorkingTimes: false});
								modal.close();
							},
							onClose: () => void deferred.reject(),
							escapeText: false,
							destroyOnClose: true
						});
					} else if (data.workingTimesForWorkScheduleModel > 0) {
						// work model change is allowed but a confirmation dialog has to be shown (not-yet balanced half/full day WorkingTime without request)
						BootstrapModal.confirm({
							title: "dialog.update.userWorkScheduleModel.title",
							message: "dialog.update.workScheduleModel.message",
							yesLabel: "dialog.update.userWorkScheduleModel.yes",
							noLabel: "confirmation.cancel",
							yesAction: (modal: BootstrapModal.Modal) => {
								deferred.resolve({applyCutsByNormalWorkingTimes: false});
								modal.close();
							},
							onClose: () => void deferred.reject()
						});
					} else {
						BootstrapModal.error({message: "dialog.message.workschedulemodel.error"});
					}
				}).fail(() => void deferred.reject());
		} else {
			deferred.resolve({applyCutsByNormalWorkingTimes: false});
		}

		return deferred.promise();
	}

	private initValues() {
		this.updateVacationValues();
		this.updateOvertimeAllowancesIncluded();
	}

	private updateCheckboxes(user: User) {
		const checkboxWorkingTimeTracking = this.dom.find("#userChangeConfigurationDialogWorkingTimeTracking");
		if (checkboxWorkingTimeTracking) {
			checkboxWorkingTimeTracking.prop("checked", user.workingTimeTracking);
		}

		const checkboxVacationAccount = this.dom.find("#userChangeConfigurationDialogVacationAccount");
		if (checkboxVacationAccount) {
			checkboxVacationAccount.prop("checked", user.vacationAccount);
		}

		const checkboxAllIn = this.dom.find("#userChangeConfigurationDialogUserOvertimeAllowanceAllIn");
		if (checkboxAllIn) {
			checkboxAllIn.prop("disabled", !user.workingTimeTracking);
			checkboxAllIn.prop("checked", user.overtimeAllowanceAllIn);
		}

		const checkboxTrustBasedWorkingTime = this.dom.find("#userChangeConfigurationDialogUserTrustBasedWorkingTime");
		if (checkboxTrustBasedWorkingTime) {
			checkboxTrustBasedWorkingTime.prop("checked", user.trustBasedWorkingTime);
		}

		const checkboxAllowClearNegativeWorkDurationBalance = this.dom.find("#userChangeConfigurationDialogUserAllowClearNegativeWorkDurationBalance");
		if (checkboxAllowClearNegativeWorkDurationBalance) {
			checkboxAllowClearNegativeWorkDurationBalance.prop("checked", user.allowClearNegativeWorkDurationBalance);
		}

		const checkboxAllInSetWorkDurationBalanceZero = this.dom.find("#userChangeConfigurationDialogUserAllInSetWorkDurationBalanceZero");
		if (checkboxAllInSetWorkDurationBalanceZero) {
			checkboxAllInSetWorkDurationBalanceZero.prop("checked", user.allInSetWorkDurationBalanceZero);
		}

		// update overtime duration included
		this.updateOvertimeAllowancesIncluded();
	}

	private setupCheckboxFieldObserver() {
		const numberOrNull = (sel: JQuery.Selector) => {
			const value = $(sel).val();
			return value === "null" ? null : Number(value);
		};

		this.fieldObserver.observeInput("#userChangeConfigurationDialogWorkingTimeTracking", "workingTimeTracking");
		this.fieldObserver.observeInput("#userChangeConfigurationDialogVacationAccount", "vacationAccount");
		this.fieldObserver.observeInput("#userChangeConfigurationDialogUserOvertimeAllowanceAllIn", "overtimeAllowanceAllIn");
		this.fieldObserver.observeInput("#userChangeConfigurationDialogUserAllowClearNegativeWorkDurationBalance", "allowClearNegativeWorkDurationBalance");
		this.fieldObserver.observeInput("#userChangeConfigurationDialogUserAllInSetWorkDurationBalanceZero", "allInSetWorkDurationBalanceZero");
		this.fieldObserver.observeInput("#userChangeConfigurationDialogUserTrustBasedWorkingTime", "trustBasedWorkingTime");
		this.fieldObserver.observeInput("#userChangeConfigurationDialogUserOvertimeDurationIncluded", "overtimeDurationIncluded", FieldObserver.getDurationFromField);
		this.fieldObserver.observeInput("#userChangeConfigurationDialogUserHolidayCalendar", "holidayCalendarId", numberOrNull);
		this.fieldObserver.observeInput("#userChangeConfigurationDialogUserVacationYearStart", "vacationYearStart", FieldObserver.getDateJsonFromField);
		this.fieldObserver.observeInput("#userChangeConfigurationDialogUserVacationDaysPerYear", "vacationDaysPerYear", FieldObserver.getDecimalNumberFromField);

		this.fieldObserver.onFieldValueChange(() => {
			this.updateCheckboxes(this.user);
			this.updateVacationValues();
		});
	}

	private updateVacationValues() {
		$("#userChangeConfigurationDialogUserVacationUnit").val(this.user.vacationUnit);
		$("#userChangeConfigurationDialogUserVacationDaysPerYear").val(formatDecimalNumber(this.user.vacationDaysPerYear));
		$("#userChangeConfigurationDialogUserVacationYearStart").val(formatDate(parseISODate(this.user.vacationYearStart)));
	}

	private updateOvertimeAllowancesIncluded() {
		const overtimeDurationIncludedField = $("#userChangeConfigurationDialogUserOvertimeDurationIncluded");
		overtimeDurationIncludedField.prop("disabled", this.user.overtimeAllowanceAllIn || !this.user.workingTimeTracking);
		if (!this.user.overtimeAllowanceAllIn && this.user.overtimeDurationIncluded !== undefined) {
			overtimeDurationIncludedField.val(formatDuration(this.user.overtimeDurationIncluded));
		} else {
			overtimeDurationIncludedField.val("");
		}

		const showOvertimeIncludedOptions = this.user.overtimeAllowanceAllIn || this.user.overtimeDurationIncluded > 0;
		$("#userChangeConfigurationDialogUserTrustBasedWorkingTime").prop("disabled", !this.user.workingTimeTracking);
		$("#userChangeConfigurationDialogUserAllInSetWorkDurationBalanceZeroContainer")
			.slideToggleBool(this.user.overtimeAllowanceAllIn)
			.find("input").prop("disabled", !this.user.workingTimeTracking);
		$("#userChangeConfigurationDialogUserOvertimeAllowanceInfo").slideToggleBool(showOvertimeIncludedOptions);
		$("#userChangeConfigurationDialogUserAllowClearNegativeWorkDurationBalanceContainer")
			.slideToggleBool(showOvertimeIncludedOptions)
			.find("input").prop("disabled", !this.user.workingTimeTracking);
		$("#userChangeConfigurationDialogDeductFromAccountContainer")
			.slideToggleBool(showOvertimeIncludedOptions)
			.find("select").prop("disabled", !this.user.workingTimeTracking);
	}

	public open() {
		this.modal.show();
	}


	private setSaveButtonLabel(label: string) {
		this.modal.getButtons("save-button").text(label);
	}

	private saveUser(button: JQuery) {
		const performSaveUser = (checkResponse: { applyCutsByNormalWorkingTimes: boolean }) => {
			Button.setLoading(button);

			const selectedWorkScheduleModelAlternatingStartWeek = this.userWorkScheduleModelAlternatingStartWeekSelect.val() as string;
			const hasStartWeekChanged = this.hasAlternatingWeeksModelSelected() && (this.hasWorkScheduleModelSelectionChanged() || this.currentWorkScheduleModelWeekNumber !== selectedWorkScheduleModelAlternatingStartWeek);
			const userData = serializeObjectToArray({
				userId: this.user.id,
				workingTimeTracking: this.user.workingTimeTracking,
				vacationAccount: this.user.vacationAccount,
				trustBasedWorkingTime: this.user.trustBasedWorkingTime,
				overtimeAllowanceAllIn: this.user.overtimeAllowanceAllIn,
				overtimeDurationIncluded: this.user.overtimeDurationIncluded,
				allowClearNegativeWorkDurationBalance: this.user.allowClearNegativeWorkDurationBalance,
				allInSetWorkDurationBalanceZero: this.user.allInSetWorkDurationBalanceZero,
				overtimeDeductionAccountOrder: this.customOvertimeDeductionAccountOrder,
				workScheduleModelId: this.workScheduleModelComboBox?.getSelectedItem().id,
				workScheduleModelAlternatingStartWeek: hasStartWeekChanged ? selectedWorkScheduleModelAlternatingStartWeek : null,
				holidayCalendarId: this.user.holidayCalendarId,
				applyCutsByNormalWorkingTimes: String(checkResponse.applyCutsByNormalWorkingTimes),
				vacationDaysPerYear: this.user.vacationDaysPerYear,
				vacationYearStart: this.user.vacationYearStart
			});

			api("admin/changeConfiguration.json")
				.post(userData)
				.done((data) => {
					this.modal.close();
					$(this).triggerHandler(UserChangeConfigurationDialogHandler.EVENT_SAVED_CONFIGURATION, data.user);
					return data;
				})
				.fail((response: ErrorResponse) => {
					if (response.responseJSON?.fieldErrors) {
						FormUtil.markFieldErrors($("#userChangeConfigurationDialogForm"), response.responseJSON.fieldErrors, true);
					}
				})
				.always(() => {
					Button.resetLoading(button);
				});
		};

		this.checkUnbalancedWorkingTimes()
			.then((response) => performSaveUser(response), () => this.modal.resetLoadingButtons());
	}

	private initDeductFromAccountSelects() {
		// Get all time account selects
		const selects: JQuery[] = [];
		$('[name="userChangeOvertimeDeductionAccountOrder"]').each((i, e) => {
			selects.push($(e));
		});

		// Set order defined by user
		this.customOvertimeDeductionAccountOrder = fillOvertimeDeductionAccountOrder(selects, this.user.overtimeDeductionAccountOrder);

		for (const select of selects) {
			select.on("change", () =>
				this.customOvertimeDeductionAccountOrder = fillOvertimeDeductionAccountOrder(
					selects,
					selects.map((e) => $(e).val() as OvertimeDeductionAccountType)
				)
			);
		}
	}

	private onWorkScheduleModelChange() {
		const isAlternatingModel = this.hasAlternatingWeeksModelSelected();
		$("#userWorkScheduleModelAlternatingStartWeekGroup").toggle(isAlternatingModel);
		if (isAlternatingModel) {
			// fill alternating start week select
			const wsm = this.workScheduleModelComboBox?.getSelectedItem();
			DOMUtil.fillSelect(
				this.userWorkScheduleModelAlternatingStartWeekSelect,
				wsm.workScheduleModelWeeks.sort((a, b) => a.weekNumber - b.weekNumber),
				false,
				(wsmw) => DOMUtil.createOption(String(wsmw.weekNumber), wsmw.name),
				true,
				false,
				this.hasWorkScheduleModelSelectionChanged() ? null : this.currentWorkScheduleModelWeekNumber
			);
		}
	}

	private hasAlternatingWeeksModelSelected() {
		const wsm = this.workScheduleModelComboBox?.getSelectedItem();
		return wsm?.workScheduleModelType === WorkScheduleModelType.ALTERNATING_WEEKS;
	}

	private hasWorkScheduleModelSelectionChanged() {
		return this.originalWorkScheduleModelId !== this.workScheduleModelComboBox?.getSelectedItem()?.id;
	}

	private loadCurrentWorkScheduleModelWeekNumber() {
		if (this.workScheduleModelComboBox.getSelectedItem()?.workScheduleModelType === WorkScheduleModelType.ALTERNATING_WEEKS) {
			const loadingIndicator = $("#userWorkScheduleModelAlternatingStartWeekLoadingIndicator");
			loadingIndicator.show();
			this.userWorkScheduleModelAlternatingStartWeekSelect.hide();
			api("admin/getWorkScheduleModelWeekNumberForUserAndDay.json")
				.get({
					userId: this.user.id,
					day: this.user.nextTimeAccountBalanceFromDate
				})
				.done((data: number | null) => {
					this.currentWorkScheduleModelWeekNumber = String(data);
					this.onWorkScheduleModelChange();
				})
				.always(() => {
					loadingIndicator.hide();
					this.userWorkScheduleModelAlternatingStartWeekSelect.show();
				});
		}
	}
}
